import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CoreService } from './core.service'; // You must implement this to provide getViewer()
import _ from 'lodash';

import * as Cesium from 'cesium';

@Injectable({
  providedIn: 'root'
})
export class TravelersService {
  private travelRecords: any[] = [];
  private travelRecordsFiltered: any[] = [];
  private airportInfo: any = {};
  private travelClusterList: any[] = [];
  private dataSource: any;
  private handlerLeftClick: any;
  private removeClusteringListener: any;
  private tracks: Record<string, any> = {};

  public travelRecordsFiltered$ = new BehaviorSubject<any[]>([]);

  constructor(
    private core: CoreService,
    private ngZone: NgZone
  ) {}

  setTravelRecords(records: any[]) {
    this.travelRecords = records;
  }

  setTravelRecordsFiltered(records: any[]) {
    this.travelRecordsFiltered = records;
    this.travelRecordsFiltered$.next(records);
  }

  setAirportInfo(info: any) {
    this.airportInfo = info;
  }

  setTravelClusterList(list: any[]) {
    this.travelClusterList = list;
  }

  getTravelRecordsByUserId(userId: string) {
    return _.filter(this.travelRecords, o => String(o.userId) === String(userId));
  }

  getTravelClusterList() {
    return this.travelClusterList;
  }

  getTravelRecordsByPnr(pnr: string) {
    return _.find(this.travelRecords, o => o.pnr === pnr);
  }

  showTravelMarkersOnMap() {
    this.removeTravelMarkersOnMap();
    const viewer = this.core.getViewer();
    this.dataSource = new Cesium.CustomDataSource('traveler-icons');
    _.forEach(this.travelRecordsFiltered, record => {
      _.forEach(record.calendarEvents, event => {
        this.dataSource.entities.add({
          position: Cesium.Cartesian3.fromDegrees(event.position.coordinates[0], event.position.coordinates[1], 0),
          billboard: {
            image: 'img/arcangel/traveler_user.png'
          },
          pnr: record.pnr,
          place: event.place,
          userId: record.userId,
          name: record.userFullName,
          type: 'individualTraveler'
        });
      });
    });

    const pinBuilder = new Cesium.PinBuilder();
    this.removeClusteringListener = this.dataSource.clustering.clusterEvent.addEventListener((clusteredEntities: any, cluster: any) => {
      cluster.label.show = false;
      cluster.billboard.show = true;
      cluster.billboard.verticalOrigin = Cesium.VerticalOrigin.BOTTOM;
      cluster.billboard.id = { type: 'travelerCluster', members: cluster.label._id };
      cluster.billboard.members = cluster.label._id;

      const pin = pinBuilder.fromText(clusteredEntities.length, Cesium.Color.GREEN, 36).toDataURL();
      cluster.billboard.image = pin;
    });

    this.dataSource.clustering.pixelRange = 20;
    this.dataSource.clustering.minimumClusterSize = 2;
    this.dataSource.clustering.enabled = true;

    viewer.dataSources.add(this.dataSource);

    setTimeout(() => {
      this.dataSource.clustering.pixelRange = 0;
      this.dataSource.clustering.pixelRange = 20;
    }, 1000);
  }

  removeClusteringListenerFn() {
    if (this.removeClusteringListener) {
      this.removeClusteringListener();
    }
  }

  removeTravelMarkersOnMap() {
    const viewer = this.core.getViewer();
    if (this.dataSource) {
      viewer.dataSources.remove(this.dataSource);
      this.dataSource = null;
    }
  }

  createLeftClickHandler() {
    if (!this.handlerLeftClick) {
      const viewer = this.core.getViewer();
      this.handlerLeftClick = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      this.handlerLeftClick.setInputAction((click: any) => {
        this.ngZone.run(() => this.leftClickAction(click));
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
  }

  removeLeftClickHandler() {
    if (this.handlerLeftClick) {
      this.handlerLeftClick.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      this.handlerLeftClick = null;
    }
  }

  addTrack(record: any) {
    if (record.routing && this.tracks[record.routing]) {
      this.removeTrack(record);
    }

    const points: number[] = [];
    _.forEach(record.routing.replace(/ +/g, '').split('-'), code => {
      if (this.airportInfo[code] && this.airportInfo[code].position) {
        const lat = this.airportInfo[code].position.coordinates[1];
        const lon = this.airportInfo[code].position.coordinates[0];
        if (lat && lon) {
          points.push(lon, lat, 1000);
        }
      }
    });

    const viewer = this.core.getViewer();
    const track = viewer.entities.add({
      name: 'track',
      type: 'TravelerTrack',
      polyline: {
        positions: Cesium.Cartesian3.fromDegreesArrayHeights(points),
        width: 3,
        material: Cesium.Color.YELLOW
      }
    });

    this.tracks[record.routing] = track;
  }

  removeTrack(record: any) {
    const viewer = this.core.getViewer();
    if (record.routing && this.tracks[record.routing]) {
      viewer.entities.remove(this.tracks[record.routing]);
      delete this.tracks[record.routing];
    }
  }

  removeAllTracks() {
    const viewer = this.core.getViewer();
    _.forEach(this.tracks, track => {
      viewer.entities.remove(track);
    });
    this.tracks = {};
  }

  private leftClickAction(click: any) {
    const viewer = this.core.getViewer();
    const pickedObjectAll = viewer.scene.drillPick(click.position);
    let clusterInfo: any[] = [];
    _.forEach(pickedObjectAll, obj => {
      if (obj && obj.id && obj.id.type === 'travelerCluster' && obj.id.members && obj.id.members.length > 0) {
        const names = _.map(obj.id.members, o => ({
          userId: o.userId,
          name: o.name,
          pnr: o.pnr,
          key: o.userId + o.pnr
        }));
        const keys = _.uniqBy(_.map(names, 'key'), key => key);
        _.forEach(keys, key => {
          const user = _.find(names, o => o.key === key);
          const name = user ? user.name : '';
          const pnr = user ? user.pnr : '';
          const userId = user ? user.userId : '';
          const ct = _.filter(names, o => o.key === key).length;
          const display = `${name} (PNR: ${pnr}) ${ct}`;
          clusterInfo.push({ userId, name, pnr, ct, display });
        });
        clusterInfo = _.orderBy(clusterInfo, 'name');
      } else if (obj && obj.id && obj.id.type === 'individualTraveler') {
        clusterInfo.push({
          userId: obj.id.userId,
          name: obj.id.name,
          pnr: obj.id.pnr,
          display: `${obj.id.name} (PNR: ${obj.id.pnr})`
        });
      }
    });

    this.travelClusterList = clusterInfo;
    // In Angular, open a modal/dialog here if needed
  }
}